import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, X, Star, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { PLANS, PlanConfig, getPrice, getPriceId, formatPlanPrice } from "@/lib/stripe";
import { toast } from "sonner";
import { MarketingNav } from "@/components/landing/MarketingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { cn } from "@/lib/utils";
import { useGeolocatedCurrency } from "@/hooks/useGeolocatedCurrency";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema, generateSoftwareAppSchema } from "@/components/SchemaMarkup";
import { Breadcrumbs } from "@/components/Breadcrumbs";

type BillingInterval = "monthly" | "yearly";

// Comparison table data
const comparisonCategories = [
  {
    key: "categoryAnalytics",
    features: [
      { key: "featureRevenueDashboard", starter: true, growth: true, pro: true, enterprise: true },
      { key: "featureFunnelAnalysis", starter: true, growth: true, pro: true, enterprise: true },
      { key: "featureSalesAnalytics", starter: "basic", growth: true, pro: true, enterprise: true },
      { key: "featureCustomDashboards", starter: false, growth: false, pro: true, enterprise: true },
    ],
  },
  {
    key: "categoryAI",
    features: [
      { key: "featureAIAdvisor", starter: false, growth: false, pro: true, enterprise: true },
      { key: "featureAIInsights", starter: false, growth: "basic", pro: true, enterprise: true },
      { key: "featureCRMAdvisor", starter: false, growth: true, pro: true, enterprise: true },
    ],
  },
  {
    key: "categoryForecast",
    features: [
      { key: "featureForecast", starter: false, growth: true, pro: true, enterprise: true },
      { key: "featureBurndown", starter: false, growth: true, pro: true, enterprise: true },
      { key: "featureSimulator", starter: false, growth: false, pro: true, enterprise: true },
      { key: "featureTargets", starter: true, growth: true, pro: true, enterprise: true },
    ],
  },
  {
    key: "categoryPlaybooks",
    features: [
      { key: "featurePlaybooks", starter: false, growth: false, pro: true, enterprise: true },
      { key: "featureSLAMonitor", starter: false, growth: true, pro: true, enterprise: true },
      { key: "featureExecutionAlerts", starter: false, growth: true, pro: true, enterprise: true },
    ],
  },
  {
    key: "categoryIntegrations",
    features: [
      { key: "featureCRMIntegrations", starter: true, growth: true, pro: true, enterprise: true },
      { key: "featureSlack", starter: false, growth: true, pro: true, enterprise: true },
      { key: "featureAPI", starter: false, growth: false, pro: true, enterprise: true },
      { key: "featureCustomIntegrations", starter: false, growth: false, pro: false, enterprise: true },
    ],
  },
  {
    key: "categoryUsers",
    features: [
      { key: "featureUsers", starter: "5", growth: "15", pro: "unlimited", enterprise: "unlimited" },
      { key: "featureRoles", starter: false, growth: true, pro: true, enterprise: true },
      { key: "featureSSO", starter: false, growth: false, pro: false, enterprise: true },
    ],
  },
  {
    key: "categorySupport",
    features: [
      { key: "featureEmailSupport", starter: true, growth: true, pro: true, enterprise: true },
      { key: "featurePrioritySupport", starter: false, growth: true, pro: true, enterprise: true },
      { key: "featureDedicatedCSM", starter: false, growth: false, pro: false, enterprise: true },
      { key: "featureOnboarding", starter: false, growth: false, pro: true, enterprise: true },
      { key: "featureSLA", starter: false, growth: false, pro: false, enterprise: true },
    ],
  },
];

function CellValue({ value, t }: { value: boolean | string; t: (k: string) => string }) {
  if (value === true) return <Check className="h-5 w-5 text-[#00C896] mx-auto" />;
  if (value === false) return <X className="h-5 w-5 text-[#1A1F3C]/20 mx-auto" />;
  if (value === "unlimited")
    return <span className="text-sm font-medium text-[#4F6CFF]">{t("landing.pricingPage.unlimited")}</span>;
  if (value === "basic")
    return <span className="text-xs font-medium text-[#F97316]">{t("landing.pricingPage.basic")}</span>;
  return <span className="text-sm font-medium text-[#1A1F3C]">{value}</span>;
}

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { currency: geoCurrency } = useGeolocatedCurrency();
  const [interval, setInterval] = useState<BillingInterval>("yearly");
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  if (searchParams.get("checkout") === "canceled") {
    toast.info(t("billing.checkoutCanceled"));
  }

  const handleSelectPlan = async (plan: PlanConfig) => {
    if (plan.id === "enterprise") {
      window.open("mailto:sales@digitais.ai?subject=Enterprise%20Plan%20Inquiry", "_blank");
      return;
    }
    const priceId = getPriceId(plan, interval, geoCurrency);
    if (!priceId) {
      toast.error(t("billing.planNotAvailable"));
      return;
    }
    setLoadingPlan(plan.id);
    try {
      if (!isAuthenticated) {
        navigate(`/signup?plan=${plan.id}&interval=${interval}`);
        return;
      }
      const { data, error } = await supabase.functions.invoke("stripe-create-checkout", {
        body: { priceId, mode: "subscription" },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("billing.checkoutError"));
    } finally {
      setLoadingPlan(null);
    }
  };

  const plans = Object.values(PLANS);
  const planOrder = ["starter", "growth", "pro", "enterprise"];

  const faqItems = Array.from({ length: 8 }, (_, i) => ({
    q: t(`landing.pricingPage.faq.q${i + 1}`),
    a: t(`landing.pricingPage.faq.a${i + 1}`),
  }));

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title={isPt ? "Preços e Planos RevOps AI" : "Pricing & Plans | RevOps AI"}
        description={isPt ? "Planos a partir de R$497/mês. Diagnósticos de funil, forecast com IA e Revenue Playbooks. Teste grátis por 7 dias." : "Plans from $99/month. Funnel diagnostics, AI-powered forecast and Revenue Playbooks. 7-day free trial."}
        path="/pricing"
        keywords={isPt ? "preços RevOps AI, planos, Revenue Intelligence" : "RevOps AI pricing, plans, Revenue Intelligence"}
      />
      <SchemaMarkup schema={[
        generateSoftwareAppSchema(isPt ? 'pt-BR' : 'en'),
        generateBreadcrumbSchema([
          { name: 'Home', url: 'https://revops.digitais.ai/' },
          { name: isPt ? 'Preços' : 'Pricing', url: 'https://revops.digitais.ai/pricing' },
        ]),
        generateFAQSchema(faqItems.map(f => ({ question: f.q, answer: f.a }))),
      ]} />
      <MarketingNav forceSolid />

      <main className="flex-1">
        {/* Hero */}
        <section className="pt-16 pb-8" style={{ background: "linear-gradient(180deg, #1A1F3C 0%, #0F1828 100%)" }}>
          <div className="container text-center">
            <div className="max-w-3xl mx-auto mb-6">
              <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Preços' : 'Pricing' }]} />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4">
              {t("landing.pricingPage.title")}
            </h1>
            <p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">{t("landing.pricingPage.subtitle")}</p>

            <div className="flex items-center justify-center gap-4 relative">
              <Label className={`${interval === "monthly" ? "text-white font-semibold" : "text-white/50"}`}>
                {t("landing.pricing.monthly")}
              </Label>
              <Switch
                checked={interval === "yearly"}
                onCheckedChange={(c) => setInterval(c ? "yearly" : "monthly")}
                className="data-[state=checked]:bg-[#00C896] data-[state=unchecked]:bg-white/30"
              />
              <Label className={`${interval === "yearly" ? "text-white font-semibold" : "text-white/50"}`}>
                {t("landing.pricing.yearly")}
              </Label>
              {interval === "yearly" && (
                <Badge className="absolute left-[calc(50%+120px)] top-1/2 -translate-y-1/2 bg-[#00C896] text-white whitespace-nowrap">
                  {t("landing.pricing.save2Months")}
                </Badge>
              )}
            </div>
          </div>
        </section>

        {/* Plan cards */}
        <section className="py-12 -mt-4">
          <div className="container">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto">
              {plans.map((plan) => {
                const price = getPrice(plan, interval, geoCurrency);
                const isPopular = plan.popular;
                const isEnterprise = plan.id === "enterprise";
                const isLoading = loadingPlan === plan.id;

                return (
                  <Card
                    key={plan.id}
                    className={cn(
                      "relative flex flex-col bg-white",
                      isPopular && "border-[#4F6CFF] border-2 shadow-xl scale-105",
                    )}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-[#4F6CFF] text-white">
                          <Star className="h-3 w-3 mr-1" />
                          {t("landing.pricing.mostPopular")}
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-xl text-[#1A1F3C]">{t(plan.nameKey)}</CardTitle>
                      <CardDescription>{t(plan.descriptionKey)}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <div className="text-center mb-6">
                        {isEnterprise ? (
                          <div className="text-2xl font-bold text-[#1A1F3C]">{t("landing.pricing.customPricing")}</div>
                        ) : (
                          <>
                            <span
                              className="text-4xl font-bold text-[#1A1F3C]"
                              style={{ fontFamily: "'JetBrains Mono', monospace" }}
                            >
                              {formatPlanPrice(price, geoCurrency)}
                            </span>
                            <span className="text-[#1A1F3C]/50">
                              {interval === "monthly" ? t("landing.pricing.perMonth") : t("landing.pricing.perYear")}
                            </span>
                            {interval === "yearly" && (() => {
                              const monthlyTotal = getPrice(plan, 'monthly', geoCurrency) * 12;
                              const yearlyTotal = getPrice(plan, 'yearly', geoCurrency);
                              const savings = monthlyTotal - yearlyTotal;
                              if (savings > 0) {
                                return (
                                  <div className="text-sm font-medium text-[#00C896] mt-1">
                                    {t('landing.pricing.saveAmount', { amount: formatPlanPrice(savings, geoCurrency) })}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </>
                        )}
                      </div>
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-[#00C896] mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-[#1A1F3C]/70">{t(feature)}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className={`w-full ${isPopular ? "bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white" : ""}`}
                        variant={isPopular ? "default" : "outline"}
                        onClick={() => handleSelectPlan(plan)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isEnterprise ? (
                          t("landing.pricing.contactSales")
                        ) : (
                          t("landing.pricing.getStarted")
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comparison table */}
        <section className="py-16" style={{ backgroundColor: "#F4F5FF" }}>
          <div className="container">
            <h2 className="text-2xl font-bold text-center text-[#1A1F3C] mb-10">
              {t("landing.pricingPage.comparisonTitle")}
            </h2>

            <div className="max-w-6xl mx-auto rounded-xl border border-[#1A1F3C]/10 bg-white overflow-hidden">
              <table className="w-full text-sm" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="text-left py-4 px-4 w-1/3 bg-white border-b border-[#1A1F3C]/10" />
                     {planOrder.map((p) => (
                       <th
                         key={p}
                         className={cn(
                           "text-center py-4 px-4 font-semibold text-[#1A1F3C] border-b border-[#1A1F3C]/10",
                           p === "growth" ? "bg-[#EEF0FF]" : "bg-white",
                         )}
                       >
                         {t(PLANS[p].nameKey)}
                       </th>
                     ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonCategories.map((cat) => (
                    <>
                      <tr key={cat.key} className="bg-[#1A1F3C]/5">
                        <td
                          colSpan={5}
                          className="py-2 px-4 font-semibold text-[#1A1F3C] text-xs uppercase tracking-wider"
                        >
                          {t(`landing.pricingPage.${cat.key}`)}
                        </td>
                      </tr>
                      {cat.features.map((feat) => (
                        <tr key={feat.key} className="border-b border-[#1A1F3C]/5">
                          <td className="py-3 px-4 text-[#1A1F3C]/70 w-1/3">{t(`landing.pricingPage.${feat.key}`)}</td>
                          {planOrder.map((p) => (
                            <td key={p} className={`text-center py-3 px-4 ${p === "growth" ? "bg-[#4F6CFF]/5" : ""}`}>
                              <CellValue value={(feat as any)[p]} t={t} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ))}
                  {/* CTA row at bottom */}
                  <tr className="border-t-2 border-[#1A1F3C]/10">
                    <td className="py-4 px-4" />
                    {planOrder.map((p) => (
                      <td key={p} className={`text-center py-4 px-4 ${p === "growth" ? "bg-[#4F6CFF]/5" : ""}`}>
                        <Button
                          size="sm"
                          className={`whitespace-nowrap inline-flex items-center gap-1 ${p === "growth" ? "bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white" : ""}`}
                          variant={p === "growth" ? "default" : "outline"}
                          onClick={() => handleSelectPlan(PLANS[p])}
                          asChild={p === "enterprise" ? true : undefined}
                        >
                          {p === "enterprise" ? (
                            <a href="mailto:sales@digitais.ai?subject=Enterprise%20Plan%20Inquiry">
                              {t("landing.pricing.contactSales")}
                            </a>
                          ) : (
                            <Link to={`/signup?plan=${p}&interval=${interval}`}>
                              {t("landing.pricing.getStarted")}
                              <ArrowRight className="ml-1 h-3 w-3 inline" />
                            </Link>
                          )}
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container max-w-3xl">
            <h2 className="text-2xl font-bold text-center text-[#1A1F3C] mb-10">{t("landing.faq.title")}</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-[#1A1F3C]/10 rounded-lg px-4">
                  <AccordionTrigger className="text-[#1A1F3C] font-medium text-left">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-[#1A1F3C]/60">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* What's included in all plans */}
        <section className="py-16 bg-[#F4F5FF]">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-[#1A1F3C] mb-4">
              {isPt ? 'O que está incluso em todos os planos' : "What's included in every plan"}
            </h2>
            <p className="text-center text-[#1A1F3C]/60 mb-8 max-w-2xl mx-auto">
              {isPt
                ? 'Independente do plano escolhido, todas as contas RevOps AI incluem funcionalidades essenciais para sua operação de receita.'
                : 'Regardless of your chosen plan, every RevOps AI account includes essential features for your revenue operation.'}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {(isPt ? [
                { title: 'Integração nativa com CRM', desc: 'Conecte HubSpot ou RD Station em minutos. Sincronização automática diária de deals, contatos, empresas e atividades — sem exportações manuais.' },
                { title: 'Dashboard de receita em tempo real', desc: 'Acompanhe receita realizada vs metas, cobertura de pipeline e tendências. Visualize a saúde da sua operação em um único painel executivo.' },
                { title: 'Análise de funil completa', desc: 'Identifique gargalos de conversão por estágio, vendedor e período. Entenda exatamente onde sua operação perde receita e por quê.' },
                { title: 'Suporte por email e onboarding guiado', desc: 'Todas as contas incluem suporte por email, documentação completa e um fluxo de onboarding passo-a-passo para começar em minutos.' },
              ] : [
                { title: 'Native CRM integration', desc: 'Connect HubSpot or RD Station in minutes. Automatic daily sync of deals, contacts, companies and activities — no manual exports.' },
                { title: 'Real-time revenue dashboard', desc: 'Track actuals vs targets, pipeline coverage and trends. See your operation health in a single executive-grade dashboard.' },
                { title: 'Full funnel analysis', desc: 'Identify conversion bottlenecks by stage, rep and period. Understand exactly where your operation leaks revenue and why.' },
                { title: 'Email support & guided onboarding', desc: 'All accounts include email support, full documentation and a step-by-step onboarding flow to get started in minutes.' },
              ]).map((item, i) => (
                <div key={i} className="p-5 rounded-xl bg-white border border-[#1A1F3C]/10">
                  <h3 className="font-semibold text-[#1A1F3C] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#1A1F3C]/60 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-[#1A1F3C]/50 mt-6">
              {isPt
                ? 'Escolha o plano ideal para o tamanho do seu time. Startups de 3 vendedores começam com o Starter. Times de 10-15 reps se beneficiam do Growth com forecast e alertas. Operações maduras de 20+ reps desbloqueiam todo o potencial com o Pro, incluindo AI Advisor, Playbooks e simulador What-If.'
                : 'Choose the ideal plan for your team size. Startups with 3 reps start with Starter. Teams of 10-15 reps benefit from Growth with forecasting and alerts. Mature operations of 20+ reps unlock full potential with Pro, including AI Advisor, Playbooks and What-If simulator.'}
            </p>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 bg-white">
          <div className="container text-center">
            <p className="text-lg text-[#1A1F3C]/60 mb-4">{t("landing.pricingPage.stillDeciding")}</p>
            <Button size="lg" className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white" asChild>
              <a href="mailto:sales@digitais.ai?subject=Schedule%20a%20Demo">{t("landing.header.bookDemo")}</a>
            </Button>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
